import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AdminLayout } from './layouts/AdminLayout';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { AccessDenied } from './pages/AccessDenied';
import { DashboardPage } from './pages/DashboardPage';
import { StaffPage } from './pages/StaffPage';
import { SanctionsPage } from './pages/SanctionsPage';
import { PlayersPage } from './pages/PlayersPage';
import { ConsolePage } from './pages/ConsolePage';
import { ServerDetailPage } from './pages/ServerDetailPage';
import { BanwordsPage } from './pages/BanwordsPage';
import { WhitelistPage } from './pages/WhitelistPage';
import { GradesPage } from './pages/GradesPage';
import { ConfigOverviewPage } from './pages/ConfigOverviewPage';
import { BlocksPage } from './pages/config/BlocksPage';
import { ItemsPage } from './pages/config/ItemsPage';
import { EntitiesPage } from './pages/config/EntitiesPage';
import { CraftsPage } from './pages/config/CraftsPage';
import { FilesPage } from './pages/config/FilesPage';
import { ServersPage } from './pages/config/ServersPage';
import { CosmeticsPage } from './pages/config/CosmeticsPage';
import { DeployRequestsPage } from './pages/DeployRequestsPage';
import { AdminPage } from './pages/AdminPage';
import { StatsPage } from './pages/StatsPage';
import { PlayerStatsPage } from './pages/PlayerStatsPage';
import { LogsPage } from './pages/LogsPage';
import { useAuthStore, Permission } from './stores/auth.store';

/**
 * Check if current URL has auth redirect params (from static HTML hack)
 */
function isAuthRedirect(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('_auth_redirect') === '1' && !!params.get('code');
}

export function App() {
  // If this is an auth redirect from the static HTML hack, show AuthCallback directly
  if (isAuthRedirect()) {
    return (
      <BrowserRouter>
        <AuthCallback />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/microsoft/recubebe-panel-redirect" element={<AuthCallback />} />

        {/* Access Denied - requires auth but shows error */}
        <Route path="/access-denied" element={<AuthenticatedRoute><AccessDenied /></AuthenticatedRoute>} />

        {/* Protected routes - requires auth + panel access permission */}
        <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="sanctions" element={<SanctionsPage />} />
          <Route path="players" element={<PlayersPage />} />
          <Route path="players/:uuid" element={<PlayersPage />} />
          <Route path="console" element={<ConsolePage />} />
          <Route path="console/:identifier" element={<ServerDetailPage />} />
          <Route path="banwords" element={<BanwordsPage />} />
          <Route path="whitelist" element={<WhitelistPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="stats/players" element={<PlayerStatsPage />} />
          <Route path="grades" element={<GradesPage />} />

          {/* Espace Configuration (in-game / packs) */}
          <Route path="config" element={<ConfigOverviewPage />} />
          <Route path="config/blocks" element={<BlocksPage />} />
          <Route path="config/items" element={<ItemsPage />} />
          <Route path="config/entities" element={<EntitiesPage />} />
          <Route path="config/crafts" element={<CraftsPage />} />
          <Route path="config/files" element={<FilesPage />} />
          <Route path="config/servers" element={<ServersPage />} />
          <Route path="config/grades" element={<GradesPage />} />
          <Route path="config/cosmetics" element={<CosmeticsPage />} />
          <Route path="logs" element={<LogsPage />} />

          {/* Espace Déploiement : demandes d'édition à valider */}
          <Route path="deploiement" element={<DeployRequestsPage />} />

          {/* Administration (users & grades) - gardé côté nav pour les admins */}
          <Route path="admin" element={<AdminPage />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Route that only requires authentication (no permission check)
 * Used for AccessDenied page
 */
function AuthenticatedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Route that requires authentication AND panel access permission
 * Redirects to AccessDenied if user doesn't have VIEW_DASHBOARD permission
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has panel access
  const hasAccess = user?.permissions?.includes(Permission.VIEW_DASHBOARD) ?? false;
  if (!hasAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
